/**
 * Backgammon Game — Main Container Component
 *
 * Orchestrates: mode selection → engine init → dice rolling →
 * move selection → execution → turn management → game over.
 *
 * Wrapped in ErrorBoundary. Uses useAuthentication().
 * Responsive layout: board + controls + status display.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { useAuthentication } from '../../hooks/useAuthentication';
import { useNotifications } from '../../hooks/useNotifications';
import { NotificationType } from '../../services/NotificationService';
import { GameSessionManager } from '../../services/GameSessionManager';
import { GameCoachService, GameContext } from '../../services/GameCoachService';
import { backgammonKnowledge } from '../../services/gameCoachKnowledge';
import { GameCoach } from '../../components/GameCoach';
import { BackgammonEngine } from '../BackgammonEngine';
import { BackgammonAI } from '../BackgammonAI';
import { BackgammonBoard } from './BackgammonBoard';
import UniversalGameModeSelector, { GameModeConfig, GameModeSelection } from '../../components/UniversalGameModeSelector';
import { UniversalGameLayout, UniversalGameLayoutConfig } from '../../components/UniversalGameLayout';
import { BackgammonErrorHandler } from '../services/BackgammonErrorHandler';
import { BackgammonStatisticsService } from '../services/BackgammonStatisticsService';
import { BackgammonGamePersistence } from '../services/BackgammonGamePersistence';
import { ErrorHandlingService } from '../../services/ErrorHandlingService';
import { NotificationService } from '../../services/NotificationService';
import { StatisticsManager } from '../../services/StatisticsManager';
import { ProfileManager } from '../../services/ProfileManager';
import { LocalStorageManager } from '../../services/LocalStorageManager';
import {
  BackgammonGameSettings,
  BackgammonGameState,
  BackgammonMove,
  PlayerSide,
  BackgammonGameProps,
  P1_BAR,
  P2_BAR,
} from '../types';
import './BackgammonGame.css';

type GameView = 'mode-select' | 'playing' | 'game-over';

const BackgammonGameInner: React.FC<BackgammonGameProps> = ({
  onExit,
  initialRoomCode,
}) => {
  const { isAuthenticated, user } = useAuthentication();
  const { addNotification } = useNotifications();

  // Game state
  const [view, setView] = useState<GameView>('mode-select');
  const [settings, setSettings] = useState<BackgammonGameSettings | null>(null);
  const [gameState, setGameState] = useState<BackgammonGameState | null>(null);

  // Refs
  const engineRef = useRef<BackgammonEngine>(new BackgammonEngine());
  const aiRef = useRef<BackgammonAI | null>(null);
  const gameSessionIdRef = useRef<string | null>(null);
  const gameSessionManager = useRef(GameSessionManager.getInstance());
  const humanSideRef = useRef<PlayerSide>('player1');
  const coachRef = useRef(new GameCoachService('Backgammon', backgammonKnowledge));

  // Services
  const errorHandlerRef = useRef<BackgammonErrorHandler | null>(null);
  const statsServiceRef = useRef<BackgammonStatisticsService | null>(null);
  const persistenceRef = useRef<BackgammonGamePersistence | null>(null);

  // Initialize services once
  useEffect(() => {
    try {
      const errorService = ErrorHandlingService.getInstance();
      const notifService = NotificationService.getInstance();
      errorHandlerRef.current = new BackgammonErrorHandler(errorService, notifService);

      const profileManager = new ProfileManager();
      const statsManager = new StatisticsManager(profileManager);
      statsServiceRef.current = new BackgammonStatisticsService(statsManager);

      const storageManager = new LocalStorageManager();
      persistenceRef.current = new BackgammonGamePersistence(storageManager);
    } catch (e) {
      console.error('Failed to initialize services:', e);
    }

    return () => {
      aiRef.current?.destroy();
    };
  }, []);

  // =========================================================================
  // Game Flow
  // =========================================================================

  const handleStartGame = useCallback(
    (newSettings: BackgammonGameSettings) => {
      const engine = engineRef.current;
      engine.initializeGame(newSettings);

      const humanSide = newSettings.playerSide || 'player1';
      humanSideRef.current = humanSide;

      setSettings(newSettings);
      setGameState(engine.getGameState());
      setView('playing');

      gameSessionIdRef.current = gameSessionManager.current.startGameSession('backgammon');

      // Set up AI if single player
      if (newSettings.mode === 'singleplayer' && newSettings.aiDifficulty) {
        aiRef.current = new BackgammonAI(newSettings.aiDifficulty);
      } else {
        aiRef.current?.destroy();
        aiRef.current = null;
      }

      addNotification({
        type: NotificationType.SUCCESS,
        title: 'Game Started',
        message: `${newSettings.mode === 'singleplayer' ? 'Single player' : 'Multiplayer'} backgammon started!`,
        duration: 2000,
      });
    },
    [addNotification]
  );

  const handleRollDice = useCallback(() => {
    const engine = engineRef.current;
    const state = engine.getGameState();

    if (state.gameStatus !== 'rolling') return;

    // In single player, only allow human to roll
    if (settings?.mode === 'singleplayer' && state.currentPlayer !== humanSideRef.current) {
      return;
    }

    const dice = engine.rollDice();
    const newState = engine.getGameState();
    setGameState(newState);

    addNotification({
      type: NotificationType.INFO,
      title: `🎲 ${state.currentPlayer === 'player1' ? 'Dark' : 'Light'} rolled`,
      message: `${dice.die1} and ${dice.die2}${dice.isDoubles ? ' — Doubles!' : ''}${newState.validMoves.length === 0 ? ' — No valid moves!' : ''}`,
      duration: 2000,
    });

    // If no valid moves, auto-advance turn
    if (newState.validMoves.length === 0) {
      setTimeout(() => {
        engine.advanceTurn();
        const advancedState = engine.getGameState();
        setGameState(advancedState);

        if (engine.isGameOver()) {
          handleGameOver(advancedState);
        } else {
          triggerAITurnIfNeeded(advancedState);
        }
      }, 1500);
    }
  }, [settings, addNotification]);

  const handlePointClick = useCallback(
    (point: number) => {
      const engine = engineRef.current;
      const state = engine.getGameState();

      if (state.gameStatus !== 'moving') return;

      // In single player, only allow human to move
      if (settings?.mode === 'singleplayer' && state.currentPlayer !== humanSideRef.current) {
        return;
      }

      const currentSelected = state.selectedPoint;

      // If clicking a point that has valid moves FROM it, select it
      const movesFromPoint = state.validMoves.filter(m => m.from === point);
      if (movesFromPoint.length > 0) {
        engine.setSelectedPoint(point);
        setGameState(engine.getGameState());
        return;
      }

      // If a point is selected and clicking a valid target, execute the move
      if (currentSelected !== null) {
        const move = state.validMoves.find(
          m => m.from === currentSelected && m.to === point
        );
        if (move) {
          executeMove(move);
          return;
        }
      }

      // Deselect
      engine.setSelectedPoint(null);
      setGameState(engine.getGameState());
    },
    [settings]
  );

  const handleBearOff = useCallback(() => {
    const engine = engineRef.current;
    const state = engine.getGameState();

    if (state.selectedPoint === null) return;

    const bearOffTarget = state.currentPlayer === 'player1' ? 0 : 25;
    const move = state.validMoves.find(
      m => m.from === state.selectedPoint && m.to === bearOffTarget
    );

    if (move) {
      executeMove(move);
    }
  }, []);

  const executeMove = useCallback(
    (move: BackgammonMove) => {
      const engine = engineRef.current;

      try {
        const result = engine.executeMove(move);
        const newState = engine.getGameState();
        setGameState(newState);

        if (result.capturedToBar) {
          addNotification({
            type: NotificationType.WARNING,
            title: '💥 Hit!',
            message: `Checker sent to the bar!`,
            duration: 1500,
          });
        }

        if (move.isBearOff) {
          addNotification({
            type: NotificationType.INFO,
            title: '✅ Borne Off',
            message: `Checker removed from the board!`,
            duration: 1500,
          });
        }

        // Check if turn is over (no more moves)
        if (newState.gameStatus === 'rolling') {
          // Auto-save
          if (persistenceRef.current && settings) {
            persistenceRef.current.autoSave(settings.mode, newState, {
              aiDifficulty: settings.aiDifficulty,
              startedAt: newState.startedAt,
              lastSavedAt: Date.now(),
            });
          }

          // Advance turn
          setTimeout(() => {
            engine.advanceTurn();
            const advancedState = engine.getGameState();
            setGameState(advancedState);

            if (engine.isGameOver()) {
              handleGameOver(advancedState);
            } else {
              triggerAITurnIfNeeded(advancedState);
            }
          }, 500);
        }
      } catch (error) {
        errorHandlerRef.current?.handleGameLogicError(
          error instanceof Error ? error.message : 'Move execution failed'
        );
      }
    },
    [addNotification, settings]
  );

  const triggerAITurnIfNeeded = useCallback(
    (state: BackgammonGameState) => {
      if (
        settings?.mode === 'singleplayer' &&
        aiRef.current &&
        state.currentPlayer !== humanSideRef.current &&
        state.gameStatus === 'rolling'
      ) {
        // AI rolls dice after a delay
        setTimeout(() => {
          const engine = engineRef.current;
          const dice = engine.rollDice();
          const rolledState = engine.getGameState();
          setGameState(rolledState);

          addNotification({
            type: NotificationType.INFO,
            title: `🎲 AI rolled`,
            message: `${dice.die1} and ${dice.die2}${dice.isDoubles ? ' — Doubles!' : ''}`,
            duration: 1500,
          });

          if (rolledState.validMoves.length === 0) {
            // No moves, advance turn
            setTimeout(() => {
              engine.advanceTurn();
              setGameState(engine.getGameState());
            }, 1000);
            return;
          }

          // AI chooses and executes moves
          aiRef.current!.scheduleMoves(rolledState, (moves) => {
            for (const move of moves) {
              try {
                engine.executeMove(move);
              } catch {
                break; // Stop if a move fails
              }
            }

            const afterMoves = engine.getGameState();
            setGameState(afterMoves);

            // Advance turn
            setTimeout(() => {
              engine.advanceTurn();
              const advancedState = engine.getGameState();
              setGameState(advancedState);

              if (engine.isGameOver()) {
                handleGameOver(advancedState);
              }
            }, 500);
          });
        }, 800);
      }
    },
    [settings, addNotification]
  );

  const handleGameOver = useCallback(
    (state: BackgammonGameState) => {
      setGameState(state);
      setView('game-over');

      if (gameSessionIdRef.current) {
        gameSessionManager.current.endGameSession(gameSessionIdRef.current, true);
        gameSessionIdRef.current = null;
      }

      persistenceRef.current?.clearAutoSave();

      // Record statistics
      if (statsServiceRef.current && settings) {
        const totalHits = state.moveHistory.flat().filter(m => m.isHit).length;
        const engine = engineRef.current;

        statsServiceRef.current.recordGameResult(
          {
            gameId: state.gameId,
            mode: settings.mode,
            difficulty: settings.aiDifficulty,
            winner: state.winner,
            totalMoves: state.moveHistory.flat().length,
            totalHits,
            isGammon: engine.isGammon(),
            duration: Math.floor((Date.now() - state.startedAt) / 1000),
            completedAt: Date.now(),
          },
          humanSideRef.current,
          isAuthenticated ? user?.id : undefined
        );
      }

      const winner = state.winner;
      const winnerLabel = winner === 'player1' ? 'Dark' : 'Light';
      const engine = engineRef.current;

      addNotification({
        type: NotificationType.SUCCESS,
        title: '🎉 Game Over!',
        message: `${winnerLabel} wins!${engine.isGammon() ? ' Gammon!' : ''} (${state.borneOff.player1}-${state.borneOff.player2})`,
        duration: 5000,
      });
    },
    [addNotification, settings, isAuthenticated, user]
  );

  const handleNewGame = useCallback(() => {
    aiRef.current?.destroy();
    aiRef.current = null;
    setView('mode-select');
    setGameState(null);
    setSettings(null);
  }, []);

  const handleExit = useCallback(() => {
    if (gameSessionIdRef.current) {
      gameSessionManager.current.endGameSession(gameSessionIdRef.current, false);
      gameSessionIdRef.current = null;
    }
    aiRef.current?.destroy();
    onExit?.();
  }, [onExit]);

  // Trigger AI if it goes first
  useEffect(() => {
    if (view !== 'playing' || !gameState || !aiRef.current) return;
    if (
      gameState.currentPlayer !== humanSideRef.current &&
      gameState.gameStatus === 'rolling'
    ) {
      triggerAITurnIfNeeded(gameState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // =========================================================================
  // Render
  // =========================================================================

  if (view === 'mode-select') {
    const backgammonConfig: GameModeConfig = {
      gameName: 'Backgammon',
      gameIcon: '🎲',
      availableModes: ['ai', 'local', 'online'],
      difficulties: [
        { id: 'easy', label: 'Easy', icon: '☀️', description: 'AI makes mistakes' },
        { id: 'medium', label: 'Medium', icon: '⭐', description: 'Balanced gameplay' },
        { id: 'hard', label: 'Hard', icon: '🏆', description: 'Expert-level AI' },
      ],
    };

    const handleModeSelection = (selection: GameModeSelection) => {
      if (selection.mode === 'ai' && selection.difficulty) {
        handleStartGame({
          mode: 'singleplayer',
          aiDifficulty: selection.difficulty as BackgammonGameSettings['aiDifficulty'],
        });
      } else if (selection.mode === 'local') {
        handleStartGame({ mode: 'local-multiplayer' });
      } else if (selection.mode === 'online') {
        handleStartGame({ mode: 'multiplayer-realtime' });
      }
    };

    return (
      <div className="backgammon-game">
        <UniversalGameModeSelector
          config={backgammonConfig}
          onStart={handleModeSelection}
          isAuthenticated={isAuthenticated}
          guestGamesRemaining={isAuthenticated ? undefined : 3}
        />
      </div>
    );
  }

  if (!gameState) return null;

  const currentPlayer = gameState.currentPlayer;
  const isHumanTurn =
    settings?.mode !== 'singleplayer' || currentPlayer === humanSideRef.current;
  const isBoardDisabled = !isHumanTurn || gameState.gameStatus === 'completed';
  const currentLabel = currentPlayer === 'player1' ? 'Dark' : 'Light';

  // Game over config
  const gameOverConfig = (view === 'game-over' && gameState.gameStatus === 'completed') ? {
    show: true,
    result: (gameState.winner === humanSideRef.current ? 'win' : 'loss') as 'win' | 'loss',
    title: '🎉 Game Over!',
    subtitle: `${gameState.winner === 'player1' ? 'Dark' : 'Light'} wins!${engineRef.current.isGammon() ? ' Gammon!' : ''} (${gameState.borneOff.player1}-${gameState.borneOff.player2})`,
    icon: '🎉',
    onPlayAgain: handleNewGame,
    onExit: handleExit,
  } : undefined;

  const layoutConfig: UniversalGameLayoutConfig = {
    gameName: 'Backgammon',
    board: (
      <div className="backgammon-game__board-area">
        {/* Roll Dice Button */}
        {gameState.gameStatus === 'rolling' && isHumanTurn && (
          <button
            className="backgammon-game__roll-btn"
            onClick={handleRollDice}
            aria-label="Roll dice"
          >
            🎲 Roll Dice
          </button>
        )}
        <BackgammonBoard
          board={gameState.board}
          currentPlayer={currentPlayer}
          dice={gameState.dice}
          validMoves={isHumanTurn ? gameState.validMoves : []}
          selectedPoint={gameState.selectedPoint}
          borneOff={gameState.borneOff}
          onPointClick={handlePointClick}
          onBearOff={handleBearOff}
          disabled={isBoardDisabled}
          gameStatus={gameState.gameStatus}
        />
      </div>
    ),
    leftPanels: [
      {
        id: 'scores',
        title: 'Scores',
        icon: '📊',
        content: (
          <div className="backgammon-game__scores-panel">
            <div className={`backgammon-game__player-info ${currentPlayer === 'player1' ? 'backgammon-game__player-info--active' : ''}`}>
              <span className="backgammon-game__player-checker backgammon-game__player-checker--dark" aria-hidden="true" />
              <span className="backgammon-game__player-label">Dark</span>
              <span className="backgammon-game__player-score">{gameState.borneOff.player1}/15</span>
            </div>
            <div className={`backgammon-game__player-info ${currentPlayer === 'player2' ? 'backgammon-game__player-info--active' : ''}`}>
              <span className="backgammon-game__player-checker backgammon-game__player-checker--light" aria-hidden="true" />
              <span className="backgammon-game__player-label">Light</span>
              <span className="backgammon-game__player-score">{gameState.borneOff.player2}/15</span>
            </div>
            <div className="backgammon-game__info">
              <span>Turn: {gameState.turnNumber + 1}</span>
              <span>Moves: {gameState.moveHistory.flat().length}</span>
            </div>
          </div>
        ),
      },
    ],
    rightPanels: settings?.mode === 'singleplayer' ? [
      {
        id: 'coach',
        title: 'AI Coach',
        icon: '🤖',
        content: (
          <GameCoach
            coach={coachRef.current}
            gameContext={gameState ? {
              gameName: 'Backgammon',
              gameState: `Turn ${gameState.turnNumber + 1}. Dark borne off: ${gameState.borneOff.player1}/15, Light borne off: ${gameState.borneOff.player2}/15. Status: ${gameState.gameStatus}.`,
              playerContext: `Playing as ${humanSideRef.current === 'player1' ? 'Dark' : 'Light'}. ${isHumanTurn ? "It's your turn." : "Waiting for AI."}`,
            } : null}
            isPlayerTurn={isHumanTurn}
            quickQuestions={['Should I hit?', 'Safe play?', 'Pip count?', 'Bear off strategy?']}
            accentColor="#8B4513"
          />
        ),
      },
    ] : [],
    statusBar: {
      turnIndicator: (
        <span>
          {currentPlayer === 'player1' ? '⚫' : '⚪'}{' '}
          {gameState.gameStatus === 'completed'
            ? 'Game Over'
            : !isHumanTurn
              ? 'AI thinking...'
              : gameState.gameStatus === 'rolling'
                ? `${currentLabel}'s turn — Roll dice`
                : `${currentLabel} — Select a move`}
        </span>
      ),
      gameStatus: gameState.gameStatus === 'completed' ? 'Game Over' : undefined,
      gameStatusType: gameState.gameStatus === 'completed' ? 'ended' : 'active',
      aiThinking: !isHumanTurn && gameState.gameStatus !== 'completed',
    },
    actions: [
      { id: 'new-game', label: 'New Game', icon: '🔄', onClick: handleNewGame, variant: 'primary' },
      { id: 'exit', label: 'Exit', icon: '🚪', onClick: handleExit, variant: 'secondary' },
    ],
    gameOver: gameOverConfig,
  };

  return (
    <div className="backgammon-game">
      <UniversalGameLayout config={layoutConfig} />
    </div>
  );
};

/** Wrapped in ErrorBoundary */
const BackgammonGame: React.FC<BackgammonGameProps> = (props) => (
  <ErrorBoundary componentName="Backgammon">
    <BackgammonGameInner {...props} />
  </ErrorBoundary>
);

export { BackgammonGame };
export default BackgammonGame;
